// src/pages/AdministrationOverview.tsx
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaExchangeAlt,
  FaGraduationCap,
  FaSchool,
  FaSlidersH,
  FaTools,
} from "react-icons/fa";

import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";

export default function AdministrationOverview() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canCrudMutations = useCan(
    "school.mutations",
    "crud"
  );
  const canManageMutations = useCan(
    "school.mutations",
    "manage"
  );
  const canSeeMutations =
    canCrudMutations || canManageMutations;

  const canReadAlumni = useCan(
    "alumni",
    "read"
  );
  const canCrudAlumni = useCan(
    "alumni",
    "crud"
  );
  const canSeeAlumni =
    canReadAlumni || canCrudAlumni;

  const canReadSchoolSettings = useCan(
    "school.settings",
    "read"
  );
  const canUpdateSchoolSettings = useCan(
    "school.settings",
    "update"
  );
  const canSeeSchoolSettings =
    canReadSchoolSettings ||
    canUpdateSchoolSettings;
  const canManageSchoolLinks = useCan(
    "school.links",
    "manage"
  );
  const canSeeSchoolAdministration = canSeeSchoolSettings || canManageSchoolLinks;

  const canReadMutationProcessing = useCan(
    "administration.mutation-processing",
    "read"
  );
  const canSeeMutationProcessing =
    canReadMutationProcessing;

  const hasVisibleSections =
    canSeeMutations ||
    canSeeMutationProcessing ||
    canSeeAlumni ||
    canSeeSchoolAdministration;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaTools}
        eyebrow={t(
          "administration.overview.category",
          {
            defaultValue: "Administration",
          }
        )}
        title={t(
          "administration.overview.title",
          {
            defaultValue: "Administration",
          }
        )}
        description={t(
          "administration.overview.description",
          {
            defaultValue:
              "Review and process administrative tasks that need follow-up.",
          }
        )}
      />

      {hasVisibleSections ? (
        <LdsOverviewSection
          title={t(
            "administration.overview.heading",
            {
              defaultValue:
                "Administrative actions",
            }
          )}
          description={t(
            "administration.overview.sections_description",
            {
              defaultValue:
                "Choose the administrative area you want to manage.",
            }
          )}
        >
          <LdsOverviewGrid>
            {canSeeMutations ? (
              <LdsOverviewCard
                icon={FaExchangeAlt}
                eyebrow={t(
                  "administration.overview.cards.mutations.category",
                  {
                    defaultValue: "Requests",
                  }
                )}
                title={t(
                  "administration.overview.cards.mutations.title",
                  {
                    defaultValue:
                      "Mutation requests",
                  }
                )}
                description={t(
                  "administration.overview.cards.mutations.description",
                  {
                    defaultValue:
                      "Review and process changes submitted by parents.",
                  }
                )}
                actionLabel={t(
                  "common.open",
                  {
                    defaultValue: "Open",
                  }
                )}
                onClick={() =>
                  navigate(
                    "/administratie/mutaties"
                  )
                }
                animationIndex={0}
              />
            ) : null}

            {canSeeMutationProcessing ? (
              <LdsOverviewCard
                icon={FaSlidersH}
                eyebrow={t(
                  "administration.overview.cards.mutation_processing.category",
                  {
                    defaultValue:
                      "Configuration",
                  }
                )}
                title={t(
                  "admin.mutation_processing.title",
                  {
                    defaultValue:
                      "Mutation processing",
                  }
                )}
                description={t(
                  "administration.overview.cards.mutation_processing.description",
                  {
                    defaultValue:
                      "Configure availability and processing mode per mutation type.",
                  }
                )}
                actionLabel={t(
                  "common.open",
                  {
                    defaultValue: "Open",
                  }
                )}
                onClick={() =>
                  navigate(
                    "/admin/manage/mutation-processing"
                  )
                }
                animationIndex={1}
              />
            ) : null}

            {canSeeAlumni ? (
              <LdsOverviewCard
                icon={FaGraduationCap}
                eyebrow={t(
                  "administration.overview.cards.alumni.category",
                  {
                    defaultValue: "Directory",
                  }
                )}
                title={t(
                  "administration.overview.cards.alumni.title",
                  {
                    defaultValue: "Alumni",
                  }
                )}
                description={t(
                  "administration.overview.cards.alumni.description",
                  {
                    defaultValue:
                      "View and manage alumni contact preferences.",
                  }
                )}
                actionLabel={t(
                  "common.open",
                  {
                    defaultValue: "Open",
                  }
                )}
                onClick={() =>
                  navigate("/reports/alumni")
                }
                animationIndex={2}
              />
            ) : null}

            {canSeeSchoolAdministration ? (
              <LdsOverviewCard
                icon={FaSchool}
                eyebrow={t(
                  "administration.overview.cards.school.category",
                  {
                    defaultValue: "Settings",
                  }
                )}
                title={t(
                  "admin.school_settings.title",
                  {
                    defaultValue: "School",
                  }
                )}
                description={t(
                  "admin.school_settings.description",
                  {
                    defaultValue:
                      "Manage school details, contact information, location and branding.",
                  }
                )}
                actionLabel={t(
                  "common.open",
                  {
                    defaultValue: "Open",
                  }
                )}
                onClick={() =>
                  navigate(
                    "/admin/manage/school"
                  )
                }
                animationIndex={3}
              />
            ) : null}
          </LdsOverviewGrid>
        </LdsOverviewSection>
      ) : (
        <LdsOverviewEmpty>
          {t(
            "administration.overview.empty",
            {
              defaultValue:
                "No administrative sections are available with your current permissions.",
            }
          )}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
}
